import { useId, useState, type ReactNode } from "react";
import { T, useT } from "../i18n";
import { useTelefon } from "../schale/geraet";
import { Knopf } from "./index";
import type { Einsatzort, Ortsart } from "../daten/typen";
import type { OrtEingabe } from "../daten/routen";
import { KM_HOECHSTENS, kmFehler, zahlLesen } from "../daten/zahlfeld";

/* ══════════════════════════════════════════════════════════════════
   Das Formular für einen Einsatzort — in der Akte und im Deckblatt.

   Bis zum 16.09.2026 stand es in `masken/kunden/Einsatzorte.tsx` und
   nur dort. Seit die Schnellerfassung einen Ort anlegt, wenn der Kunde
   keinen hat (`OrtAnlegen.tsx`), braucht es eine zweite Stelle — und
   zwei Formulare für dieselben sieben Felder laufen auseinander, sobald
   eines ein Feld bekommt.

   **Zwei Formen, ein Formular.** `zeile` klappt unter dem Ort in der
   Akte auf (`.ort-form`), `blatt` füllt ein Deckblatt mit Fuß
   (`.fdialog`) — dieselbe Unterscheidung wie bei `KundeFormular`.
   ══════════════════════════════════════════════════════════════════ */

const ARTEN: Ortsart[] = ["baustelle", "pv_freiflaeche", "pv_dach", "betriebshof"];

export type OrtEntwurf = {
  bezeichnung: string;
  strasse: string;
  plz: string;
  ort: string;
  art: Ortsart;
  zufahrt: string;
  hinweis: string;
  km: string;
};

export const ORT_LEER: OrtEntwurf = {
  bezeichnung: "", strasse: "", plz: "", ort: "",
  art: "baustelle", zufahrt: "", hinweis: "", km: "",
};

/* ══════════════ Wie ein Ort in einer Zeile steht ══════════════

   Luca, 16.09.2026: *„Wenn ich einen von den Standorten anlege und auswähle,
   dann ist das nicht richtig angezeigt darunter."* Zwei Dinge stimmten nicht,
   und beide stecken in den zwei Funktionen hier:

   1. `wo` ist Postleitzahl und Ort, mehr nicht (`daten/routen.ts`,
      `anschriftZeile`) — „49393 Lohne" ist keine Anschrift, zu der ein Fahrer
      fährt. Die Straße gehört davor.
   2. Die Entfernung kam als nackte Zahl durch und las sich englisch:
      `String(8.4)` ergibt „8.4", und darunter stand „8.4 km".

   **Sie stehen hier und nicht in einer Maske**, seit die Vermietfläche
   dieselbe Zeile zeigt wie die alte Schnellerfassung (18.09.2026,
   docs/38-vermietmodul.md, Punkt 5): Zwei Fassungen derselben Anschrift
   laufen auseinander, sobald eine ein Feld bekommt. */

/** Straße und Ort in einer Zeile. Leer, wenn keine Anschrift hinterlegt ist. */
export function anschriftVon(o: Einsatzort): string {
  const ortszeile = [o.plz, o.ort].filter(Boolean).join(" ").trim();
  return [o.strasse?.trim(), ortszeile || o.wo.trim()].filter(Boolean).join(", ");
}

/** `8.4` → `8,4`. Die Zahl kommt als Text aus der Akte, das Komma ist deutsch. */
export function kmText(km: string): string {
  const zahl = zahlLesen(km);
  return zahl !== null
    ? zahl.toLocaleString("de-DE", { maximumFractionDigits: 1 })
    : km;
}

export function ortAusEinsatzort(o: Einsatzort): OrtEntwurf {
  return {
    bezeichnung: o.name,
    strasse: o.strasse ?? "",
    plz: o.plz ?? "",
    ort: o.ort ?? "",
    art: o.art ?? "baustelle",
    zufahrt: o.zufahrt ?? "",
    hinweis: o.notiz ?? "",
    km: o.km ?? "",
  };
}

/** Was die Route bekommt — ohne `standard`, den entscheidet der Aufrufer. */
export function ortFelder(e: OrtEntwurf): Omit<OrtEingabe, "standard"> {
  return {
    bezeichnung: e.bezeichnung.trim(),
    anschrift: { strasse: e.strasse.trim(), plz: e.plz.trim(), ort: e.ort.trim() },
    art: e.art,
    zufahrt: e.zufahrt.trim() || null,
    hinweis: e.hinweis.trim() || null,
    entfernung_km: zahlLesen(e.km),
  };
}

export function Ortsformular({
  anfang, sichern, abbrechen, laeuft, form = "zeile", schmutzigAendern, unten, rahmen,
}: {
  anfang: OrtEntwurf;
  sichern: (e: OrtEntwurf) => void;
  abbrechen: () => void;
  laeuft: boolean;
  form?: "zeile" | "blatt";
  /** Ob etwas angefangen ist — für die Nachfrage des Deckblatts. */
  schmutzigAendern?: (wert: boolean) => void;
  /** Was unter den Feldern steht, über dem Fuß — ein Fehlerband etwa. */
  unten?: ReactNode;
  /**
   * **Die dritte Form: der Aufrufer setzt den Rahmen selbst** (18.09.2026).
   *
   * Luca, nach dem Durchklicken der Kundenakte am Telefon: *„Bei Einsatzorten
   * ist einfach nur ein Formular, was aufgeht. Mach damit auch das Bottom
   * Sheet."* Ein Blatt von unten trägt seinen Fuß selbst (`fuss`), und der
   * bleibt stehen, während die Felder rollen — es kann ihn also nicht als
   * `<footer>` INNEN geliefert bekommen.
   *
   * Statt einer dritten Zeichnung hier bekommt der Aufrufer beides in die
   * Hand: die Felder und die Knöpfe. Wohin er sie hängt, ist seine Sache.
   * Die sieben Felder bleiben damit an einer Stelle — und das war der Grund,
   * aus dem es diesen Baustein gibt.
   */
  rahmen?: ((felder: ReactNode, knoepfe: ReactNode) => ReactNode) | undefined;
}) {
  const t = useT();
  const id = useId();
  const telefon = useTelefon();
  const [f, setzeF] = useState(anfang);
  const setzen = (k: keyof OrtEntwurf, v: string) => {
    setzeF((alt) => ({ ...alt, [k]: v }));
    schmutzigAendern?.(true);
  };
  const kmProblem = kmFehler(f.km);

  const felder = (
    <>
      <div className="frow">
        <label htmlFor={`${id}-name`}>{t("einsatzort.bezeichnung")}</label>
        {/* **Auf dem Telefon kein Erstfokus** (18.09.2026). Am Schreibtisch
            spart er einen Klick; am Telefon fährt die Tastatur hoch, bevor
            jemand das Blatt gesehen hat, und deckt die halbe Maske zu —
            dieselbe Entscheidung wie bei der Notiz (`handy/Notiz.tsx`). */}
        <input
          className="fein"
          id={`${id}-name`}
          autoFocus={!telefon}
          placeholder={t("kundeNeu.ortNamePlatz")}
          value={f.bezeichnung}
          /* Dieselbe Grenze wie die API (V-05, 03.10.2026). */
          maxLength={120}
          onChange={(e) => setzen("bezeichnung", e.target.value)}
        />
      </div>
      <div className="frow">
        <label htmlFor={`${id}-art`}>{t("einsatzort.artSchild")}</label>
        <select
          className="fein"
          id={`${id}-art`}
          value={f.art}
          onChange={(e) => setzen("art", e.target.value)}
        >
          {ARTEN.map((a) => <option key={a} value={a}>{t(`einsatzort.art.${a}`)}</option>)}
        </select>
      </div>
      <div className="frow">
        <label htmlFor={`${id}-strasse`}>{t("kundeNeu.strasse")}</label>
        <input
          className="fein"
          id={`${id}-strasse`}
          value={f.strasse}
          onChange={(e) => setzen("strasse", e.target.value)}
        />
      </div>
      <div className="frow">
        <label htmlFor={`${id}-plz`}>{t("kundeNeu.plzOrt")}</label>
        <div className="plz-ort">
          <input
            className="fein mono"
            id={`${id}-plz`}
            inputMode="numeric"
            aria-label={t("kundeNeu.plz")}
            value={f.plz}
            onChange={(e) => setzen("plz", e.target.value)}
          />
          <input
            className="fein"
            aria-label={t("kundeNeu.ort")}
            value={f.ort}
            onChange={(e) => setzen("ort", e.target.value)}
          />
        </div>
      </div>
      <div className="frow">
        <label htmlFor={`${id}-km`}>{t("einsatzort.entfernung")}</label>
        {/* Die Einheit steht im Feld und nicht daneben: Eine Zahl ohne
            Einheit ist keine Entfernung, und „km" hinter dem Feld liest
            sich beim Tippen nicht mit. */}
        <input
          className="fein mono"
          id={`${id}-km`}
          inputMode="decimal"
          placeholder={t("einsatzort.kmPlatz")}
          value={f.km}
          aria-invalid={kmProblem ? true : undefined}
          onChange={(e) => setzen("km", e.target.value)}
        />
      </div>
      {/* K-19: Was keine Entfernung ist, sagt das Feld — statt still zu leeren. */}
      {kmProblem ? (
        <div className="fhint acht" role="alert">
          {t(kmProblem === "unlesbar" ? "einsatzort.kmUnlesbar" : "einsatzort.kmBereich", { hoechstens: KM_HOECHSTENS })}
        </div>
      ) : null}
      <div className="frow">
        <label htmlFor={`${id}-zufahrt`}>{t("einsatzort.zufahrt")}</label>
        <input
          className="fein"
          id={`${id}-zufahrt`}
          placeholder={t("einsatzort.zufahrtPlatz")}
          value={f.zufahrt}
          onChange={(e) => setzen("zufahrt", e.target.value)}
        />
      </div>
      <div className="frow">
        <label htmlFor={`${id}-hinweis`}>{t("einsatzort.hinweis")}</label>
        <input
          className="fein"
          id={`${id}-hinweis`}
          placeholder={t("kundeNeu.ortHinweisPlatz")}
          value={f.hinweis}
          onChange={(e) => setzen("hinweis", e.target.value)}
        />
      </div>
      <div className="fhint"><T k="einsatzort.hinweisSatz" /></div>
      {unten}
    </>
  );

  /* **Ein Fuß, zwei Knöpfe, rechts — der Hauptknopf ganz rechts.** Die Reihe
     ist in beiden Formen dieselbe; nur die Größe unterscheidet sich, und das
     mit Grund: In der Akte klappt das Formular unter einer Zeile auf und
     trägt `klein` wie alles dort, im Deckblatt trägt der Blattfuß die
     gewöhnliche Größe wie jeder andere (28-designkonzept.md). */
  const knoepfe = (klein: boolean) => (
    <>
      <Knopf klein={klein} art="stumm" onClick={abbrechen}>{t("allgemein.abbrechen")}</Knopf>
      <Knopf
        klein={klein}
        art="haupt"
        disabled={!f.bezeichnung.trim() || laeuft || kmProblem !== null}
        onClick={() => sichern(f)}
      >
        {t("allgemein.speichern")}
      </Knopf>
    </>
  );

  // Der Rahmen des Aufrufers geht vor: Er weiß, ob er ein Blatt von unten
  // füllt oder etwas anderes, und wohin darin der Fuß gehört.
  if (rahmen) return <>{rahmen(felder, knoepfe(false))}</>;

  if (form === "blatt") {
    return (
      <div className="fdialog ort-blatt">
        <div className="fb">{felder}</div>
        <footer>{knoepfe(false)}</footer>
      </div>
    );
  }

  return (
    <div className="ort-form">
      {felder}
      <div className="ort-form-fuss">{knoepfe(true)}</div>
    </div>
  );
}
